import React from "react";

import { Text } from "@performyard/py-components";
import { ProgressBar } from "@performyard/py-components";

import { useReviewCycleBuilderPageContext } from "../../hooks/ReviewCycleBuilderPageContext";

import BaseReviewCycleDetailsCard from "../BaseReviewCycleDetailsCard";

type FormCompletionCardProps = {
	width: Parameters<typeof BaseReviewCycleDetailsCard>[0]["width"];
};

const FormCompletionCard = ({ width }: FormCompletionCardProps) => {
	const { reviewCycleReviewsDetails } = useReviewCycleBuilderPageContext();

	// Filter out closed reviews
	const activeReviews = reviewCycleReviewsDetails?.filter((review) => !review.closed) || [];

	// Sum stats from all reviews for Authors and Signers sections
	const statCounters = activeReviews.reduce(
		(acc, review) => {
			acc.totalAuthorSteps += review.authorTurnStats.total;
			acc.completedAuthorSteps += review.authorTurnStats.completed;
			acc.totalSignerSteps += review.signerTurnStats.total;
			acc.completedSignerSteps += review.signerTurnStats.completed;
			// Sum form counts from each review (excludes closed reviews)
			acc.totalForms += review.totalFormsCount ?? 0;
			acc.completedForms += review.completedFormsCount ?? 0;
			return acc;
		},
		{
			totalAuthorSteps: 0,
			completedAuthorSteps: 0,
			totalSignerSteps: 0,
			completedSignerSteps: 0,
			totalForms: 0,
			completedForms: 0,
		}
	);

	const totalAuthorSteps = statCounters.totalAuthorSteps;
	const completedAuthorSteps = statCounters.completedAuthorSteps;
	const totalSignerSteps = statCounters.totalSignerSteps;
	const completedSignerSteps = statCounters.completedSignerSteps;

	// Form count should be based on the total number of forms (review reports) across all active reviews
	const totalForms = statCounters.totalForms;
	const completedForms = statCounters.completedForms;

	return (
		<BaseReviewCycleDetailsCard width={width}>
			<div className="flex flex-col gap-4">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					Form Completion
				</Text>
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-1">
						<div className="flex justify-between">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Form Completion
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{completedForms}/{totalForms} (
								{totalForms > 0 ? ((completedForms / totalForms) * 100).toFixed(0) : 0}
								%)
							</Text>
						</div>
						<ProgressBar color="green" value={totalForms > 0 ? (completedForms / totalForms) * 100 : 0} />
					</div>
					<div className="flex flex-col gap-1">
						<div className="flex justify-between">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Authors
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{completedAuthorSteps}/{totalAuthorSteps} (
								{totalAuthorSteps > 0
									? ((completedAuthorSteps / totalAuthorSteps) * 100).toFixed(0)
									: 0}
								%)
							</Text>
						</div>
						<ProgressBar
							color="green"
							value={totalAuthorSteps > 0 ? (completedAuthorSteps / totalAuthorSteps) * 100 : 0}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<div className="flex justify-between">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Signers
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{completedSignerSteps}/{totalSignerSteps} (
								{totalSignerSteps > 0
									? ((completedSignerSteps / totalSignerSteps) * 100).toFixed(0)
									: 0}
								%)
							</Text>
						</div>
						<ProgressBar
							color="green"
							value={totalSignerSteps > 0 ? (completedSignerSteps / totalSignerSteps) * 100 : 0}
						/>
					</div>
				</div>
			</div>
		</BaseReviewCycleDetailsCard>
	);
};

export default FormCompletionCard;
